import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getOverrideCode } from "@/data/rcCodeOverrides";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section → code prefix used for that section in rc_codes
const SECTION_PREFIX = {
  jacket: "C",
  pant: "T",
  vest: "V",
  shirt: "S",
};

// Pretty-print a snake_case field name
function humanizeField(field, section) {
  let name = field;
  const prefix = `${section}_`;
  if (name.startsWith(prefix)) name = name.slice(prefix.length);
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Renders a Select for every rc_codes field of the given section that isn't
 * already covered by the main, manually-wired styling UI. Each option shows
 * the value plus its factory code badge (e.g., "Half canvas · C-000B"),
 * mirroring the look of the primary StyleSelect.
 *
 * Selected value is written into the parent's section styling state under the
 * field's snake_case key.
 *
 * Props:
 *   section — "jacket" | "pant" | "vest" | "shirt"
 *   styling — current section state object
 *   setStyling — setter (next styling)
 *   excludeFields — array of field names to omit (the ones already rendered
 *     by the main UI). The component never re-renders manually-wired fields.
 *   title — header label (default: "More Style Details")
 *   testIdPrefix — base data-testid prefix
 */
export default function AutoStyleFields({
  section,
  styling,
  setStyling,
  excludeFields = [],
  hideFieldsMatching = [],
  title = "More Style Details",
  testIdPrefix,
}) {
  const [fieldsByName, setFieldsByName] = useState({}); // { field: [{code,value}] }
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/rc-codes`)
      .then((res) => {
        if (cancelled) return;
        const grouped = {};
        const prefix = SECTION_PREFIX[section];
        for (const row of res.data?.rows || []) {
          // Section filter: jacket rows are C-*, pant T-*, vest V-*, shirt S-*
          if (!row.code || !prefix || !row.code.startsWith(`${prefix}-`)) continue;
          if (!grouped[row.field]) grouped[row.field] = [];
          grouped[row.field].push({ code: row.code, value: row.value });
        }
        // De-dupe options within each field by value (keep first code)
        for (const f of Object.keys(grouped)) {
          const seen = new Set();
          grouped[f] = grouped[f].filter((o) => {
            const k = (o.value || "").toLowerCase();
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
          });
        }
        setFieldsByName(grouped);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [section]);

  const visibleFields = useMemo(() => {
    const exclude = new Set(excludeFields);
    return Object.keys(fieldsByName)
      .filter((f) => {
        if (exclude.has(f)) return false;
        if (!fieldsByName[f]?.length) return false;
        const lower = f.toLowerCase();
        for (const pat of hideFieldsMatching) {
          if (lower.includes(pat.toLowerCase())) return false;
        }
        return true;
      })
      .sort();
  }, [fieldsByName, excludeFields, hideFieldsMatching]);

  if (!loaded || visibleFields.length === 0) return null;

  return (
    <div className="mt-3 pt-3 border-t border-white/5" data-testid={testIdPrefix}>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">
        {title} <span className="text-slate-500">({visibleFields.length})</span>
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {visibleFields.map((field) => {
          const opts = fieldsByName[field];
          const current = styling?.[field] || "";
          // Selected option's code (live lookup via override store)
          const selectedCode =
            current && getOverrideCode(field, current)
              ? getOverrideCode(field, current)
              : opts.find((o) => o.value === current)?.code || null;
          return (
            <div key={field}>
              <label className="text-[10px] text-slate-500 block mb-0.5 truncate">
                {humanizeField(field, section)}
              </label>
              <Select
                value={current || undefined}
                onValueChange={(v) => setStyling({ ...styling, [field]: v })}
              >
                <SelectTrigger
                  className="bg-[#0a1628] border-white/10 h-8 text-xs"
                  data-testid={`${testIdPrefix}-${field}-trigger`}
                >
                  <SelectValue placeholder="—">
                    <span className="flex items-center gap-1.5 truncate">
                      <span className="truncate">{current || "—"}</span>
                      {selectedCode && (
                        <code className="text-[10px] text-amber-400/80 font-mono shrink-0">
                          {selectedCode}
                        </code>
                      )}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {opts.map((o) => (
                    <SelectItem key={o.code} value={o.value} className="text-xs">
                      <span className="flex items-center gap-2 w-full">
                        <span className="flex-1 truncate">{o.value}</span>
                        <code className="text-[10px] text-amber-400/80 font-mono">
                          {o.code}
                        </code>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>
    </div>
  );
}
